import {
  BulkProgress,
  DeployChunk,
  DeploymentCenterActionRequest,
  DeploymentCenterActionResult,
  DeploymentCenterStackRow,
  DeploymentCenterStackType,
  DeploymentPlan,
  DeploymentSkipReason,
  newVersionPlanStackIds,
  StackGroupState,
  NewVersionPlan,
} from '@pubstack/common/src/deployment-center'
import { PaginatedResponse } from '@pubstack/common/src/pagination'
import { sumBy } from 'lodash-es'
import { BulkUpdateStacksResult } from '@pubstack/common/src/stack'
import { api } from '~/api/HttpProvider'
import { buildUrlWithSearchParams } from '~/utils/url'
import { useQuery } from '../api-client'

export const DEPLOYMENT_CENTER_QUERY_KEYS = (scopeId: string) => [scopeId, 'deployment-center']

const ENDPOINT = '/deployment-center'

type StackQueue = Exclude<StackGroupState, 'up-to-date'>

type StackListResponse = {
  counts: Record<StackQueue, number>
  stacks: PaginatedResponse<DeploymentCenterStackRow>
}

const EMPTY_LIST: StackListResponse = {
  counts: { 'out-of-sync': 0, 'to-deploy': 0 },
  stacks: { data: [], pagination: { page: 1, limit: 25, total: 0, totalPages: 1 } },
}

/**
 * One page of a queue, plus both tab counts.
 *
 * The counts ride along with the rows rather than coming from their own request: they are read out of the
 * same aggregation, under the same Node gate, so a badge cannot advertise rows its table would hide.
 */
export const useDeploymentCenterStacks = ({
  scopeId,
  tab,
  search,
  type,
  page,
  limit,
  sortBy,
  sortOrder,
  enabled = true,
}: {
  scopeId: string
  tab: StackQueue
  search?: string
  type?: DeploymentCenterStackType
  page: number
  limit: number
  sortBy?: string
  sortOrder?: 'ascending' | 'descending'
  enabled?: boolean
}) => {
  const url = buildUrlWithSearchParams(`${ENDPOINT}/stacks`, { tab, search: search || undefined, type, page, limit, sortBy, sortOrder })
  const query = useQuery<StackListResponse>({
    url,
    keys: [...DEPLOYMENT_CENTER_QUERY_KEYS(scopeId), { tab, search, type, page, limit, sortBy, sortOrder }],
    enabled,
    // The table should not blank out between pages or tab switches — the counts stay readable while the
    // next page loads, which is what keeps the action button's total stable under the cursor.
    keepPreviousDataEnabled: true,
  })

  return { ...query, data: query.data ?? EMPTY_LIST }
}

/** What the server would write, without writing it. Imperative: it backs an explicit click, not a render. */
export const fetchDeployPlan = async (request: Omit<DeploymentCenterActionRequest, 'tab'>): Promise<DeploymentPlan> => {
  const { data } = await api.post<DeploymentPlan>(`${ENDPOINT}/deploy/plan`, { ...request, tab: 'to-deploy' })
  return data
}

const skipReasonOfError = (error: string): DeploymentSkipReason => (error === 'Outdated stack snapshot' ? 'outdated' : error === 'Inconsistent scope' ? 'wrong-scope' : 'not-found')

/**
 * Apply a plan, one of its chunks per request.
 *
 * Deliberately not one call: API Gateway stops a request at 29s, and a thousand-stack run is minutes of
 * work — ~9s per 100 stacks for updated versions, ~2s per 100 for a deploy, measured on a large
 * production scope. The server cuts the plan to keep roughly a 2x margin on the slower of the two, so
 * this walks the chunks it was handed rather than deciding a size of its own.
 *
 * Sequential rather than parallel: every chunk lands on the same scope, and each one triggers a status
 * recompute over the medias it touched, so firing them together would multiply that work rather than
 * divide the wait. Sequencing is also what makes the progress figure mean anything.
 */
export const runDeployPlan = async (plan: DeploymentPlan, onProgress?: (progress: BulkProgress) => void): Promise<DeploymentCenterActionResult> => {
  // The plan holds one promotion per group plus an archive write per version it replaces, but the user
  // confirmed a number of groups. Progress, the success count and the skips therefore only ever count
  // promotions — an archive write failing on a group whose promotion landed is not a skipped group.
  const promotions = (chunk: DeployChunk): number => Object.keys(chunk.groupByPromotionId).length
  const total = sumBy(plan.chunks, promotions)
  const skipped = [...plan.skipped]
  let succeeded = 0
  let done = 0

  for (const [index, chunk] of plan.chunks.entries()) {
    const groupOf = (stackId: string): string | undefined => (Object.hasOwn(chunk.groupByPromotionId, stackId) ? chunk.groupByPromotionId[stackId] : undefined)
    let data: BulkUpdateStacksResult
    try {
      // Only the request is guarded: a failure reading the response back is a bug, not a stopped run.
      ;({ data } = await api.post<BulkUpdateStacksResult>(`${ENDPOINT}/deploy/apply`, { updates: chunk.updates }))
    } catch {
      // One promotion per group, so the promotions left to send are the groups never reached. The throwing
      // chunk's own count too: whether the server applied them before the connection died is unknowable here.
      return { resolved: plan.resolved, succeeded, skipped, stopped: { notAttempted: sumBy(plan.chunks.slice(index), promotions) } }
    }
    succeeded += data.successful.filter((id) => groupOf(id) !== undefined).length
    for (const { stackId, error } of data.failed) {
      const stackGroupId = groupOf(stackId)
      if (stackGroupId) skipped.push({ stackGroupId, reason: skipReasonOfError(error) })
    }
    done += promotions(chunk)
    onProgress?.({ phase: 'applying', done, total })
  }

  return { resolved: plan.resolved, succeeded, skipped }
}

/** What creating updated versions would copy, without copying it. */
export const fetchNewVersionPlan = async (request: Omit<DeploymentCenterActionRequest, 'tab'>): Promise<NewVersionPlan> => {
  const { data } = await api.post<NewVersionPlan>(`${ENDPOINT}/update/plan`, { ...request, tab: 'out-of-sync' })
  return data
}

/**
 * Apply an update plan, one of its chunks per request.
 *
 * The mirror image of `runDeployPlan`, down to the pacing, and deliberately not the same payload: a
 * deploy sends the writes themselves, while this sends only the ids and the server does the copying. A
 * new version carries the whole configuration of the one it replaces, so shipping those to the browser
 * and straight back would be the page's largest payload, twice, for data that never needed to leave.
 */
export const runNewVersionPlan = async (plan: NewVersionPlan, onProgress?: (progress: BulkProgress) => void): Promise<DeploymentCenterActionResult> => {
  const total = newVersionPlanStackIds(plan).length
  let succeeded = 0
  let done = 0

  for (const chunk of plan.chunks) {
    const { data } = await api.post<{ created: number }>(`${ENDPOINT}/update/apply`, chunk)
    succeeded += data.created
    done += chunk.stackIds.length
    onProgress?.({ phase: 'applying', done, total })
  }

  return { resolved: plan.resolved, succeeded, skipped: plan.skipped }
}
